import { useMutation, useQuery, useQueryClient, type UseQueryResult } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { fetchAppointments, fetchFollowups, fetchTasks } from "../../api/home";
import { fetchBills, fetchCheckins, fetchOccurrences, fetchPayments } from "../../api/lists";
import { useAppContext, useSessionSnapshot } from "../../app/permissions";
import { useServices } from "../../app/services";
import { addDays, today as todayOf } from "../../lib/dates";

export type Range = "today" | "yesterday";

/** Everything a Home card needs to scope a call: the business-local date, the branch filter, and who is asking. */
export function useHomeScope() {
  const { now } = useServices();
  const ctx = useAppContext();
  const s = useSessionSnapshot();
  // Home stays open all day at the desk: the "Now" line, greeting and business date must move without a reload.
  const [, setMinute] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setMinute((n) => n + 1), 60_000);
    return () => clearInterval(id);
  }, []);
  const date = todayOf(now(), ctx.timeZone);
  const branchId = s.branchFilter === "all" ? undefined : s.branchFilter;
  // Every key carries business + branch + filter, so a cached list can never be read in another context.
  const scopeKey = [s.businessId, s.branchId, s.branchFilter] as const;
  return { ctx, date, branchId, scopeKey, nowMs: now() };
}

export type Scope = ReturnType<typeof useHomeScope>;

export function useFollowups(sc: Scope) {
  const { client } = useServices();
  return useQuery({ queryKey: ["followups", ...sc.scopeKey, sc.date], queryFn: () => fetchFollowups(client, { date: sc.date, branchId: sc.branchId }), retry: false });
}
export function useAppointments(sc: Scope) {
  const { client } = useServices();
  return useQuery({ queryKey: ["appointments", ...sc.scopeKey, sc.date], queryFn: () => fetchAppointments(client, { date: sc.date, branchId: sc.branchId }), retry: false });
}
export function useTasks(sc: Scope) {
  const { client } = useServices();
  return useQuery({ queryKey: ["tasks", ...sc.scopeKey, sc.date], queryFn: () => fetchTasks(client, { date: sc.date, branchId: sc.branchId }), retry: false });
}

const rangeDate = (sc: Scope, r: Range) => (r === "today" ? sc.date : addDays(sc.date, -1));
/** Same weekday, a week earlier: the fair comparison for a gym (Monday vs Monday). */
export const comparisonDate = (sc: Scope, r: Range) => addDays(rangeDate(sc, r), -7);

export function useRangeData(sc: Scope, range: Range) {
  const { client } = useServices();
  const { ctx } = sc;
  const cur = rangeDate(sc, range);
  const prev = comparisonDate(sc, range);
  // D57: the collected figure is `revenue.home_collections`, bill counts `billing.bills_view`, visits the check-in log.
  // The figure reads GET /payments, which the server gates on `billing.payments_view`: both levers, or the 403 fails the whole strip.
  const canPay = ctx.has("revenue.home_collections") && ctx.has("billing.payments_view");
  const canBills = ctx.has("billing.bills_view");
  const canVisits = ctx.has("members.checkins");
  const key = (name: string, d: string) => [name, ...sc.scopeKey, d];
  const payments = (d: string) => ({ queryKey: key("payments", d), queryFn: () => fetchPayments(client, { from: d, to: d, branchId: sc.branchId }), enabled: canPay, retry: false });
  const bills = (d: string) => ({ queryKey: key("bills", d), queryFn: () => fetchBills(client, { from: d, to: d, branchId: sc.branchId }), enabled: canBills, retry: false });
  const checkins = (d: string) => ({ queryKey: key("checkins", d), queryFn: () => fetchCheckins(client, { date: d, branchId: sc.branchId }), enabled: canVisits, retry: false });
  return {
    cur, prev, canPay, canBills, canVisits,
    paymentsCur: useQuery(payments(cur)), paymentsPrev: useQuery(payments(prev)),
    billsCur: useQuery(bills(cur)), billsPrev: useQuery(bills(prev)),
    checkinsCur: useQuery(checkins(cur)), checkinsPrev: useQuery(checkins(prev)),
  };
}

/** Bills still owing money: unpaid and part-paid, first page of each. Needs `billing.dues`, and `billing.bills_view` for the GET /bills it reads. */
export function useDues(sc: Scope) {
  const { client } = useServices();
  const enabled = sc.ctx.has("billing.dues") && sc.ctx.has("billing.bills_view");
  const unpaid = useQuery({ queryKey: ["bills", ...sc.scopeKey, "unpaid"], queryFn: () => fetchBills(client, { paymentStatus: "unpaid", branchId: sc.branchId }), enabled, retry: false });
  const partial = useQuery({ queryKey: ["bills", ...sc.scopeKey, "partial"], queryFn: () => fetchBills(client, { paymentStatus: "partial", branchId: sc.branchId }), enabled, retry: false });
  const data = useMemo(() => (unpaid.data && partial.data ? { bills: [...unpaid.data.bills, ...partial.data.bills], capped: unpaid.data.nextCursor !== null || partial.data.nextCursor !== null } : undefined), [unpaid.data, partial.data]);
  return { enabled, data, isPending: enabled && (unpaid.isPending || partial.isPending), error: unpaid.error ?? partial.error, refetch: () => { void unpaid.refetch(); void partial.refetch(); } };
}

export function useOccurrences(sc: Scope) {
  const { client } = useServices();
  return useQuery({
    queryKey: ["occurrences", ...sc.scopeKey, sc.date],
    queryFn: () => fetchOccurrences(client, { from: sc.date, to: sc.date, branchId: sc.branchId }),
    enabled: sc.ctx.has("classes.view"),
    retry: false,
  });
}

/** Re-reads a list after a write settles. */
export function useRefresh(name: string) {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: [name] });
}

export const errorOf = (q: UseQueryResult<unknown>) => q.error;
export { useMutation };
